import { select, Selection, BaseType } from 'd3-selection';
import { seriesSvgMulti, AnySelection, AnySeries } from '@d3fc/d3fc-series';
import { annotationSvgLine } from '@d3fc/d3fc-annotation';
import {
    BreaksRenderType,
    fallsWithinBreak,
    timeBreakRectWidth,
} from './timebreaks';
import { TimePointer } from './TimePointer';
import {
    TimeSeriesDisableRange,
    TimeSeriesDisableRangeLabel,
} from 'utils/timeseries/TimeSeriesDisableRange';
import { convertToMilliseconds } from './timeSeriesTimeScale';
import styleConstants from './variables.module.scss';
import { DateTime } from 'luxon';
import { KeyedStreamItem } from 'communication/types/StreamElement';
import { timeFormatConstants } from '@instinet-analytics/core-lib';
import classNames from 'classnames';
const { timeMinutes, date } = timeFormatConstants;

/* eslint-disable @typescript-eslint/no-explicit-any */

const lineColor = styleConstants.timelineColour;
const textColor = styleConstants.timelineTextColour;
const nowBackgroundColor = styleConstants.nowLabelBackgroundColor;
const nowTextColor = styleConstants.nowLabelTextColor;

export interface TimeLineLabel {
    /*
     * The time this label is for
     */
    time: number;
    /*
     * The time from we render this label (to cater for x-axis rounding and padding)
     */
    from: number;
    /*
     * The time on the x-axis after to we render this label (to cater for x-axis rounding and padding)
     */
    to: number;
    label: string;
}

export type RenderTimeLineFunction = (
    node: any,
    id: string,
    isDomainValid: boolean,
    timeBreaks: TimeSeriesDisableRange[],
    breakType: BreaksRenderType
) => void;

export function renderTimeLine(
    timePointer: TimePointer
): RenderTimeLineFunction {
    return (
        node: any,
        id: string,
        isDomainValid: boolean,
        timeBreaks: TimeSeriesDisableRange[],
        breakType: BreaksRenderType
    ) => {
        const l = select(node)
            .select('.plot-area')
            .select('svg')
            .selectAll('line.time-line')
            .raise()
            .data([1]);

        const lineSelection = l
            .enter()
            .append('line')
            .raise()
            .attr('class', 'time-line')
            .attr('stroke', lineColor)
            .attr('stroke-width', '1.5px')
            .merge(l as any);

        l.exit().remove();

        return timePointer.subscribe(`${id}-timeline`, (context) => {
            const visible =
                isDomainValid &&
                !!context &&
                !!context.x &&
                !!context.time &&
                !fallsWithinBreak(context.time, timeBreaks, breakType);
            const visibility = visible ? 'visible' : 'hidden';

            const className = classNames('time-line', context?.className);

            lineSelection
                .attr('x1', context?.x ?? 0)
                .attr('y1', 0)
                .attr('x2', context?.x ?? 0)
                .attr('y2', '100%')
                .attr('class', className)
                .style('visibility', visibility);
        });
    };
}

export type RenderTimeRectFunction = (
    selection: any,
    id: string,
    width: number,
    displayXAxis: boolean,
    xScale: any,
    startTime: number,
    endTime: number,
    isDomainValid: boolean,
    timeBreaks: TimeSeriesDisableRange[],
    breaksType: BreaksRenderType,
    rangeLabels: TimeSeriesDisableRangeLabel[],
    timeLineLabels: TimeLineLabel[],
    showDate: boolean
) => void;

export function renderTimeRect(
    timePointer: TimePointer
): RenderTimeRectFunction {
    return (
        selection: any,
        id: string,
        width: number,
        displayXAxis: boolean,
        xScale: any,
        startTime: number,
        endTime: number,
        isDomainValid: boolean,
        timeBreaks: TimeSeriesDisableRange[],
        breaksType: BreaksRenderType,
        rangeLabels: TimeSeriesDisableRangeLabel[],
        timeLineLabels: TimeLineLabel[],
        showDate: boolean
    ) => {
        const firstTick = selection.node();
        if (!firstTick) {
            return;
        }

        const {
            rectSelection: nowRectSelection,
            textSelection: nowTextSelection,
            tspanSelection: nowTspanSelection,
            lineSelection: nowLineSelection,
        } = createTimeLineLabel(
            firstTick,
            'now',
            'nowtime',
            false,
            nowBackgroundColor,
            nowTextColor
        );

        const now = Date.now();
        const isNowVisible =
            displayXAxis && isDomainValid && startTime <= now && now <= endTime;
        const nowXPosition = isNowVisible
            ? computeNowXPosition(
                  now,
                  startTime,
                  endTime,
                  width,
                  timeBreaks,
                  breaksType
              )
            : undefined;

        nowTspanSelection.text('Now');
        setTimeLineLabelPosition(
            nowRectSelection,
            nowTextSelection,
            nowTspanSelection,
            nowLineSelection,
            nowXPosition,
            width,
            isNowVisible
        );

        const { rectSelection, textSelection, tspanSelection, lineSelection } =
            createTimeLineLabel(firstTick, 'hh-mm', 'timeline', showDate);

        return timePointer.subscribe(`${id}-timerect`, (context) => {
            const timeLineLabel = getTimeLineLabel(
                timeLineLabels,
                context?.time
            );

            const rangeLabel = getRangeLabel(
                context && context.x,
                xScale,
                rangeLabels
            );

            if (context && context.time) {
                const text = getLabelWithTime(
                    timeLineLabel,
                    rangeLabel,
                    context.time,
                    showDate
                );

                tspanSelection.text(text);
            }

            const visible =
                displayXAxis &&
                isDomainValid &&
                !!context &&
                !!context.x &&
                context.x >= 0 &&
                !!context.time;

            setTimeLineLabelPosition(
                rectSelection,
                textSelection,
                tspanSelection,
                lineSelection,
                context && context.x,
                width,
                visible
            );
        });
    };
}

export function getLabelWithTime(
    timeLineLabel: TimeLineLabel | undefined,
    rangeLabel: { text: string; value: number } | undefined,
    timestamp: number,
    showDate?: boolean
) {
    const d = new Date(timeLineLabel?.time || rangeLabel?.value || timestamp);
    const dt = DateTime.fromJSDate(d);
    const time = dt.toFormat(timeMinutes).toLowerCase();
    const dateStr = dt.toFormat(date);
    const label = timeLineLabel?.label || rangeLabel?.text;
    return `${label ? label + ' ' : ''}${time}${showDate ? ' ' + dateStr : ''}`;
}

export function createChartSeries(
    chartSeries: AnySeries,
    timeLineLabels: TimeLineLabel[]
): AnySeries {
    const sessionBoundaryAnnotationSeries = annotationSvgLine()
        .orient('vertical')
        .value((d: TimeLineLabel) => d.time)
        .label(() => '')
        .decorate((selection: AnySelection) => {
            selection.classed('timebreak-line', true);
        });

    return seriesSvgMulti()
        .series([chartSeries, sessionBoundaryAnnotationSeries])
        .mapping((data: any[], index: number, series: AnySeries[]) => {
            switch (series[index]) {
                case sessionBoundaryAnnotationSeries:
                    return timeLineLabels;
                default:
                    return data;
            }
        });
}

function getRangeLabel(
    x: number | undefined,
    xScale: any,
    rangeLabels: TimeSeriesDisableRangeLabel[]
): { text: string; value: number } | undefined {
    if (!x) {
        return undefined;
    }

    for (const label of rangeLabels) {
        if (!label.close && x! <= Math.round(xScale(label.open) + 1)) {
            // Open boundary...
            return { text: label.openLabel, value: label.open };
        }

        const xClose = Math.round(xScale(label.close));
        const xOpen = Math.round(xScale(label.close) + 5 + 1);

        if (x! >= xClose && x! <= xOpen) {
            const mid = xClose + (xOpen - xClose) / 2;
            if (x < mid) {
                return label.closeLabel
                    ? { text: label.closeLabel, value: label.close! }
                    : undefined;
            } else {
                return label.openLabel
                    ? { text: label.openLabel, value: label.open }
                    : undefined;
            }
        }
    }

    return undefined;
}

function getTimeLineLabel(
    timeLineLabels: TimeLineLabel[],
    time?: number
): TimeLineLabel | undefined {
    if (!time) {
        return undefined;
    }

    const isWithin = (timeLineLabel: TimeLineLabel, time: number) => {
        return time >= timeLineLabel.from && time <= timeLineLabel.to;
    };

    const timeLineLabel = timeLineLabels.find((l) => isWithin(l, time!));
    return timeLineLabel;
}

export interface SessionLabelledTimeSeriesItem extends KeyedStreamItem {
    si: string;
}

export const timeLabelPadding = 29999;
export function computeTimeLabels<T extends SessionLabelledTimeSeriesItem>(
    timeSeries: T[],
    breaksInTrading: TimeSeriesDisableRange[],
    tradingBreakProximityLimit: number
): TimeLineLabel[] {
    return timeSeries
        .map((val) => ({
            time: convertToMilliseconds(val._i),
            label: val.si,
        }))
        .filter(
            (val) =>
                !isNearTradingBreak(
                    val.time,
                    breaksInTrading,
                    tradingBreakProximityLimit
                )
        )
        .map((val) => ({
            ...val,
            from: val.time - timeLabelPadding,
            to: val.time + timeLabelPadding,
        }));
}

function isNearTradingBreak(
    currentTimeInMs: number,
    breaksInTrading: TimeSeriesDisableRange[],
    proximityLimit: number
) {
    for (let x = 0; x < breaksInTrading.length; ++x) {
        const tradingBreak = breaksInTrading[x];

        const toleranceStart = tradingBreak.from - proximityLimit;
        const toleranceEnd = tradingBreak.to + proximityLimit;

        if (
            toleranceStart < currentTimeInMs &&
            currentTimeInMs < toleranceEnd
        ) {
            return true;
        }
    }

    return false;
}

const indicatorHeight = 2;
function createTimeLineLabel(
    firstTick: any,
    labelGroupType: string,
    labelSubType: string,
    showDate: boolean,
    backgroundColor: string = lineColor,
    labelTextColor: string = textColor
): {
    rectSelection: Selection<BaseType, number, any, unknown>;
    textSelection: Selection<BaseType, number, any, unknown>;
    tspanSelection: Selection<BaseType, number, any, unknown>;
    lineSelection: Selection<BaseType, number, any, unknown>;
} {
    const g = select(firstTick)
        .select(function (this: any) {
            return this.parentNode;
        })
        .selectAll(`g.${labelGroupType}`)
        .data([1]);

    const gEnter = g.enter().append('g').raise().attr('class', labelGroupType);

    const gSelection = gEnter.merge(g as any);
    g.exit().remove();

    gEnter.append('rect').attr('class', `${labelSubType}-rect`);
    const textEnter = gEnter
        .append('text')
        .attr('class', `${labelSubType}-text`);
    textEnter.append('tspan').attr('class', `${labelSubType}-tspan`);
    gEnter.append('line').attr('class', `${labelSubType}-line`);

    const yOffset = indicatorHeight;
    const rectSelection = gSelection.select(`rect.${labelSubType}-rect`);
    rectSelection
        .attr('fill', backgroundColor)
        .attr('y', yOffset)
        .attr('height', 14 - yOffset);

    const textSelection = gSelection.select(`text.${labelSubType}-text`);
    textSelection
        .attr('fill', labelTextColor)
        .attr('font-size', '10px')
        .attr('y', 11)
        .attr('align-baseline', 'middle');

    const lineSelection = gSelection.select(`line.${labelSubType}-line`);
    lineSelection.attr('stroke', backgroundColor).attr('stroke-width', '1.5px');

    return {
        rectSelection,
        textSelection,
        tspanSelection: textSelection.select(`tspan.${labelSubType}-tspan`),
        lineSelection,
    };
}

function setTimeLineLabelPosition(
    rectSelection: Selection<BaseType, number, any, unknown>,
    textSelection: Selection<BaseType, number, any, unknown>,
    tspanSelection: Selection<BaseType, number, any, unknown>,
    lineSelection: Selection<BaseType, number, any, unknown>,
    xPosition: number | undefined,
    plotAreaWidth: number,
    visible: boolean
): void {
    const rectWidth: number =
        (tspanSelection?.node() as any).getComputedTextLength() + 5;

    const halfRectWidthAdj = rectWidth / 2;

    let rectMiddleX = xPosition || -1 * halfRectWidthAdj;
    let lineX = rectMiddleX + 1;
    rectMiddleX = Math.max(rectMiddleX, halfRectWidthAdj);
    rectMiddleX = Math.min(rectMiddleX, plotAreaWidth - halfRectWidthAdj);
    lineX = Math.max(lineX, 0);
    lineX = Math.min(lineX, plotAreaWidth);

    const visibility = visible ? 'visible' : 'hidden';
    rectSelection
        .attr('x', rectMiddleX - halfRectWidthAdj)
        .attr('width', rectWidth)
        .style('visibility', visibility);
    textSelection.attr('x', rectMiddleX).style('visibility', visibility);

    lineSelection
        .attr('x1', lineX)
        .attr('y1', -2)
        .attr('x2', lineX)
        .attr('y2', indicatorHeight)
        .style('visibility', visibility);
}

export function computeNowXPosition(
    now: number,
    startTime: number,
    endTime: number,
    plotAreaWidth: number,
    timeBreaks: TimeSeriesDisableRange[],
    breaksType: BreaksRenderType
) {
    const fullyElapsedTimeBreaksX = timeBreaks.reduce(
        (res, val) => (res += now > val.to ? timeBreakRectWidth : 0),
        0
    );

    const partiallyElapsedTimeBreakX = timeBreaks.reduce(
        (res, val) =>
            res +
            (fallsWithinBreak(now, [val], breaksType)
                ? ((now - val.from) * timeBreakRectWidth) / (val.to - val.from)
                : 0),

        0
    );

    let start = startTime;
    let elapsedTimeExcludingBreaks = 0;
    const sortedTimeBreaks = timeBreaks.sort((a, b) => a.from - b.from);
    for (let x = 0; x < sortedTimeBreaks.length; ++x) {
        const timeBreak = sortedTimeBreaks[x];

        if (start < now && now < timeBreak.from) {
            break;
        } else {
            elapsedTimeExcludingBreaks += timeBreak.from - start;
        }

        start = timeBreak.to;
    }

    if (now > start) {
        elapsedTimeExcludingBreaks += now - start;
    }

    const totalTradeBreakTime = timeBreaks.reduce(
        (res, val) => res + (val.to - val.from),
        0
    );
    const totalTimeExcludingBreaks = endTime - startTime - totalTradeBreakTime;

    const totalTimeBreakX = timeBreaks.length * timeBreakRectWidth;
    const marketTimeExcludingBreaksX = plotAreaWidth - totalTimeBreakX;

    const elapsedTimeExcludingBreaksX =
        (elapsedTimeExcludingBreaks * marketTimeExcludingBreaksX) /
        totalTimeExcludingBreaks;

    return Math.floor(
        elapsedTimeExcludingBreaksX +
            fullyElapsedTimeBreaksX +
            partiallyElapsedTimeBreakX
    );
}
